import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Stack,
  Grid,
  Divider,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  Badge,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Tag as TagIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  AccountBalance as AccountIcon,
  Source as SourceIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  LocalOffer as OfferIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';

const statusColors = {
  open: 'primary',
  won: 'success',
  lost: 'error',
  'on-hold': 'warning',
};

const stageColors = {
  Qualification: 'default',
  Proposal: 'primary',
  Negotiation: 'warning',
  'Closed Won': 'success',
  'Closed Lost': 'error',
};

function OpportunityDetail({
  opportunity, onEdit, onBack, onDelete, onToggleWatched,
}) {
  const [activeTab, setActiveTab] = useState(0);

  if (!opportunity) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="h6">Select an opportunity to view details.</Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (amount, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  function TabPanel({
    children, value, index, ...other
  }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`opportunity-tabpanel-${index}`}
        aria-labelledby={`opportunity-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3,
      }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {opportunity.title}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={opportunity.stage}
              color={stageColors[opportunity.stage] || 'default'}
              variant="outlined"
            />
            <Chip
              label={opportunity.status}
              color={statusColors[opportunity.status] || 'default'}
            />
            {opportunity.source && (
              <Chip
                label={opportunity.source}
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={onBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <IconButton onClick={onEdit} color="primary">
            <EditIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MoneyIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(opportunity.value, opportunity.currency)}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Deal Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {opportunity.probability || 0}
                  %
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Win Probability
              </Typography>
              <LinearProgress
                variant="determinate"
                value={opportunity.probability || 0}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {formatDate(opportunity.expectedCloseDate)}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Expected Close Date
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Details" />
          <Tab label="Timeline" />
          <Tab label="Notes" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {opportunity.description || 'No description provided.'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Products/Services
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(opportunity.products || []).map((product, index) => (
                <Chip key={index} label={product} variant="outlined" />
              ))}
              {(opportunity.products || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No products specified
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(opportunity.tags || []).map((tag, index) => (
                <Chip key={index} label={tag} size="small" />
              ))}
              {(opportunity.tags || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No tags assigned
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Details Tab */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Stage</Typography>
            <Typography variant="body1" gutterBottom>{opportunity.stage}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Typography variant="body1" gutterBottom>{opportunity.status}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Value</Typography>
            <Typography variant="body1" gutterBottom>
              {formatCurrency(opportunity.value, opportunity.currency)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Currency</Typography>
            <Typography variant="body1" gutterBottom>{opportunity.currency || 'USD'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Probability</Typography>
            <Typography variant="body1" gutterBottom>
              {opportunity.probability || 0}
              %
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Source</Typography>
            <Typography variant="body1" gutterBottom>{opportunity.source || 'Not specified'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Expected Close Date</Typography>
            <Typography variant="body1" gutterBottom>{formatDate(opportunity.expectedCloseDate)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Actual Close Date</Typography>
            <Typography variant="body1" gutterBottom>{formatDate(opportunity.actualCloseDate)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
            <Typography variant="body1" gutterBottom>
              {opportunity.notes || 'No notes available.'}
            </Typography>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Timeline Tab */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h6" gutterBottom>
          Opportunity Timeline
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CalendarIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Created"
              secondary={formatDate(opportunity.createdAt)}
            />
          </ListItem>
          {opportunity.expectedCloseDate && (
            <ListItem>
              <ListItemIcon>
                <TimelineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Expected Close"
                secondary={formatDate(opportunity.expectedCloseDate)}
              />
            </ListItem>
          )}
          {opportunity.actualCloseDate && (
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Closed"
                secondary={formatDate(opportunity.actualCloseDate)}
              />
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <AssessmentIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Last Updated"
              secondary={formatDate(opportunity.updatedAt)}
            />
          </ListItem>
        </List>
      </TabPanel>

      {/* Notes Tab */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {opportunity.notes || 'No notes available for this opportunity.'}
        </Typography>
      </TabPanel>
    </Paper>
  );
}

export default OpportunityDetail;
